import React, { useEffect } from 'react';

import { FlexColumn, FlexRow } from '@editor/components/utilities/layouts';
import analytics from '@repo/analytics';
import type { Subscription } from '@repo/subscription/api';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
  BillingAdminAccess,
} from '@repo/subscription/client';
import { H3, P2 } from '@repo/ui/components/Typography';
import { Logo } from '@repo/ui/deprecated/Logo';
import { Modal } from '@repo/ui/deprecated/Modal';
import { themeStyle } from '@repo/ui/theming';
import { capitalizeFirstLetter } from '@repo/ui/utils';

import {
  PAYMENT_FAILED_MODAL_DOWNGRADE,
  PAYMENT_FAILED_MODAL_SHOW,
  PAYMENT_FAILED_MODAL_SKIP_CLOSE,
  PAYMENT_FAILED_MODAL_SUCCESS,
} from '../../../global/analytics/events';
import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '../../../stores/planSelectionStore';
import { userStoreSelector, useUserStore } from '../../../stores/userStore';
import { formatMonetaryValue } from '../../../utils/billing';
import { PaymentError, usePaymentStatus } from '../../Payment';
import { HideDesktop, HideMobile } from '../../utilities/queries';
import useNextCharge from '../hooks/useNextCharge';
import useSubscriptionPreview from '../hooks/useSubscriptionPreview';
import OffboardingModal from '../OffboardingModal/OffboardingModal';
import PlanPaymentDetails from '../SubscriptionModal/PlanPurchaseDetails/PlanPaymentDetails';
import SubscriptionSuccessModal from '../SubscriptionSuccessModal/SubscriptionSuccessModal';

import { useModalControl } from './hooks';
import {
  Container,
  ContentWrapper,
  HeaderGrid,
  HeaderWrapper,
  LinkButton,
  Wrapper,
} from './styles';

enum Modals {
  Offboarding,
  Success,
}

const PaymentFailedModal: React.FC = () => {
  const { canClose, shouldShow, closeVariation, triggerUpdate } =
    useModalControl();

  const user = useUserStore(userStoreSelector.user);
  const currentSubscription = useSubscriptionStore(
    subscriptionStoreSelector.subscription
  );

  const { setFromSubscription } = usePlanSelectionStore(
    planSelectionStoreSelector.actions
  );

  const [shownModal, setShownModal] = React.useState<Modals | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const onClose = (): void => {
    setShownModal(null);
    setIsOpen(false);

    // Triger re-evaluation of cookie conditions. Equivalent to setting shouldShow to false
    triggerUpdate();
  };
  useEffect((): void => {
    if (!isOpen && shouldShow && user && currentSubscription?.pastDue) {
      setFromSubscription(currentSubscription);
      setIsOpen(true);
      void analytics.track(PAYMENT_FAILED_MODAL_SHOW, {
        userId: user.id,
      });
    }
  }, [currentSubscription, setFromSubscription, shouldShow, user, isOpen]);

  if (currentSubscription === null || user === null) {
    return null;
  }

  return (
    <>
      <Modal
        externalCloseButton
        noBorderRadius
        height="100vh"
        isOpen={isOpen}
        theme={{ padding: '0px', zIndex: themeStyle.zIndexM9 }}
        width="100vw"
      >
        {currentSubscription && (
          <PaymentFailedModalContent
            canClose={canClose}
            closeVariation={closeVariation}
            currentSubscription={currentSubscription}
            onClose={(): void => {
              onClose();
              void analytics.track(PAYMENT_FAILED_MODAL_SKIP_CLOSE, {
                userId: user.id,
              });
            }}
            onDowngrade={(): void => {
              setShownModal(Modals.Offboarding);
              void analytics.track(PAYMENT_FAILED_MODAL_DOWNGRADE, {
                userId: user.id,
              });
            }}
            onPaymentSuccess={(): void => {
              setShownModal(Modals.Success);
              void analytics.track(PAYMENT_FAILED_MODAL_SUCCESS, {
                userId: user.id,
              });
            }}
          />
        )}
      </Modal>
      {shownModal === Modals.Success && (
        <SubscriptionSuccessModal
          singlePayment
          isOpen={shownModal === Modals.Success}
          theme={{ zIndex: themeStyle.zIndexL9 }}
          onClose={onClose}
        />
      )}
      {shownModal === Modals.Offboarding && (
        <OffboardingModal
          skipToCancel
          backButtonLabel="Back"
          isOpen={shownModal === Modals.Offboarding}
          modalProps={{ theme: { zIndex: themeStyle.zIndexL9 } }}
          subscription={currentSubscription}
          user={user}
          onClose={(): void => setShownModal(null)}
          onSuccess={onClose}
        />
      )}
    </>
  );
};

const PageHeader: React.FC<
  React.PropsWithChildren<{ height: number; logoHeight?: number }>
> = ({ children, height, logoHeight }) => (
  <HeaderWrapper $height={height}>
    <HeaderGrid>
      <FlexRow alignItems="center" gap="12px">
        <Logo height={logoHeight} />
      </FlexRow>
      <FlexRow alignItems="center">{children}</FlexRow>
    </HeaderGrid>
  </HeaderWrapper>
);

const PaymentFailedModalContent: React.FC<{
  currentSubscription: Subscription;
  canClose: boolean;
  closeVariation: 'close' | 'downgrade' | null;
  onClose: () => void;
  onPaymentSuccess: () => void;
  onDowngrade: () => void;
}> = ({
  canClose,
  closeVariation,
  onClose,
  currentSubscription,
  onDowngrade,
  onPaymentSuccess,
}) => {
  const subscriptionChangePreviewData = useSubscriptionPreview({
    currentSubscription,
  });

  const nextCharge = useNextCharge({
    currentSubscription,
  });
  const paymentStatus = usePaymentStatus();

  let paymentMethod = '';
  if (currentSubscription?.card) {
    paymentMethod = `made with ${capitalizeFirstLetter(currentSubscription.card.brand)} ···· ${currentSubscription.card.last4} card`;
  } else if (currentSubscription?.payPal) {
    paymentMethod = 'made with PayPal';
  }

  const formattedChargeAmount = nextCharge?.value
    ? formatMonetaryValue(nextCharge?.value)
    : null;

  const closeElement = <LinkButton onClick={onClose}>Do this later</LinkButton>;
  const downgradeElement = (
    <LinkButton onClick={onDowngrade}>Downgrade to Free Plan</LinkButton>
  );
  const topRightContent = canClose && (
    <>
      {closeVariation === 'close' && closeElement}
      {closeVariation === 'downgrade' && downgradeElement}
    </>
  );

  return (
    <Wrapper alignItems="flex-start">
      <HideDesktop width="100%">
        <PageHeader height={58} logoHeight={25}>
          {topRightContent}
        </PageHeader>
      </HideDesktop>
      <HideMobile width="100%">
        <PageHeader height={72}>{topRightContent}</PageHeader>
      </HideMobile>
      <ContentWrapper>
        <Container>
          <H3 margin="0 0 12px 0">We couldn't process your last payment.</H3>
          <BillingAdminAccess
            fallback={
              <P2>
                Your team's subscription is currently managed by another user.
                <br />
                Please contact them to update the subscription.
              </P2>
            }
          >
            <P2>
              Your last payment of {formattedChargeAmount} for Kittl Pro{' '}
              {paymentMethod} didn't go through. To keep your subscription,
              please settle your bill.
            </P2>

            <FlexColumn marginBottom="24px" marginTop="24px">
              {paymentStatus.error ? (
                <PaymentError type="subscription" />
              ) : (
                <PlanPaymentDetails
                  layout="accordion"
                  subscriptionChangePreviewData={subscriptionChangePreviewData}
                  onPaymentSuccess={onPaymentSuccess}
                />
              )}
            </FlexColumn>
          </BillingAdminAccess>
        </Container>
      </ContentWrapper>
    </Wrapper>
  );
};

export default PaymentFailedModal;
